import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { authService } from '@/services';

interface NavigationProps {
  minimal?: boolean;
}

export default function Navigation({ minimal = false }: NavigationProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      try {
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);
        if (authenticated) {
          const currentUser = authService.getCurrentUser();
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
        setUser(null);
        // Clear potentially corrupted data
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
      }
    };
    
    checkAuth();
    // Check auth on storage change (e.g., login in another tab)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const handleLogout = async () => {
    try {
      // Clear local state immediately for better UX
      setIsAuthenticated(false);
      setUser(null);
      
      // Call logout service (which will clear localStorage and redirect)
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: manually clear and redirect if service fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      navigate('/sign-in');
    }
  };

  return (
    <nav className="fixed w-full top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-12">
            <Link
              to="/"
              target={minimal ? "_blank" : undefined}
              rel={minimal ? "noopener noreferrer" : undefined}
              className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent hover:opacity-80 transition-opacity"
            >
              AI MASTERY HUB
            </Link>
            {!minimal && (
              <div className="hidden md:flex space-x-8 text-sm">
                <Link to="/courses" className="text-gray-300 hover:text-white transition">
                  Courses
                </Link>
                <Link to="/bundles" className="text-gray-300 hover:text-white transition">
                  Bundles
                </Link>
                <Link to="/ultimate-prompt-bundle" className="text-amber-400 hover:text-amber-300 transition font-semibold">
                  Prompts
                </Link>
                {/* <a href="#subscriptions" className="text-gray-300 hover:text-white transition">
                  Subscriptions
                </a> */}
                {isAuthenticated && (
                  <Link to="/dashboard" className="text-blue-400 hover:text-blue-300 transition font-semibold">
                    Student Dashboard
                  </Link>
                )}
                {isAuthenticated && user?.role === 'admin' && (
                  <Link to="/admin/products" className="text-purple-400 hover:text-purple-300 transition font-semibold">
                    Admin
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-sm text-gray-300 hover:text-white transition flex items-center space-x-2"
                >
                  <User className="w-4 h-4" />
                  <span>{user?.username || 'Dashboard'}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="bg-gradient-to-r from-red-600 to-pink-600 px-4 py-2 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/sign-in" 
                  className="text-sm text-gray-300 hover:text-white transition"
                >
                  Sign In
                </Link>
                <Link 
                  to="/sign-up" 
                  className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-2 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all inline-block"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}